/**
 * Client workouts hub. Ported from the original app's WorkoutsList.tsx —
 * same UI verbatim, with next/link → react-router Link and the api client.
 * Reads GET /api/workouts/programs.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { ExerciseLibrary } from './ExerciseLibrary';

type Tab = 'programs' | 'library';

interface Program {
  id: string;
  name: string;
  description: string | null;
  duration_weeks: number;
  workouts_per_week: number;
  difficulty: string;
  current_week: number | null;
  workout_count: number;
}

export default function WorkoutsList() {
  const { user } = useAuth();
  const userId = user?.id;

  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('programs');

  useEffect(() => {
    api
      .get<{ programs: Program[] }>('/api/workouts/programs')
      .then((data) => setPrograms(data.programs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <>
      <PageHeader title="Workouts" subtitle="Your training & exercises" />
      <div className="px-4 flex flex-col gap-4" style={{ minWidth: 0 }}>
      {/* Tab bar */}
      <div className="flex rounded-[var(--radius-sm)] overflow-hidden" style={{ border: '1px solid var(--card-border)' }}>
        {(['programs', 'library'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className="flex-1 py-2 text-sm font-semibold"
            style={{
              background: tab === t ? 'var(--gold)' : 'transparent',
              color: tab === t ? '#1a1a1a' : 'var(--text-tertiary)',
            }}
          >
            {t === 'programs' ? 'Programs' : 'Library'}
          </button>
        ))}
      </div>

      {tab === 'library' ? (
        <ExerciseLibrary />
      ) : loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="card animate-pulse h-24" style={{ background: 'var(--surface)' }} />
          ))}
        </div>
      ) : (
        <>
      {/* Action buttons */}
      <div className="grid grid-cols-3 gap-2">
        <Link to="/workouts/custom" className="btn-outline text-center text-sm py-2">
          Custom Workout
        </Link>
        <Link to="/workouts/cardio" className="btn-outline text-center text-sm py-2">
          Log Cardio
        </Link>
        <Link to="/workouts/history" className="btn-outline text-center text-sm py-2">
          PR History
        </Link>
      </div>

      {/* Programs */}
      {programs.length === 0 ? (
        <div className="card text-center py-8">
          <p className="text-3xl mb-2">&#127947;</p>
          <p className="text-white font-semibold">No Programs Yet</p>
          <p className="text-sm mt-1" style={{ color: 'var(--gray-400)' }}>
            Your trainer will assign programs to you
          </p>
        </div>
      ) : (
        programs.map((program) => (
          <Link key={program.id} to={`/workouts/program/${program.id}`}>
            <div className="card cursor-pointer transition-all hover:border-[var(--gold-muted)]">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white font-semibold">{program.name}</p>
                  {program.description && (
                    <p className="text-sm mt-0.5" style={{ color: 'var(--gray-400)' }}>
                      {program.description}
                    </p>
                  )}
                </div>
                <span
                  className="text-xs px-2 py-1 rounded-full font-medium"
                  style={{
                    background: 'var(--surface)',
                    color: 'var(--gold)',
                  }}
                >
                  {program.difficulty}
                </span>
              </div>
              <div className="flex gap-4 mt-3 text-xs" style={{ color: 'var(--gray-400)' }}>
                <span>{program.duration_weeks} weeks</span>
                <span>{program.workouts_per_week}x/week</span>
                <span>{program.workout_count} workouts</span>
                {program.current_week && (
                  <span style={{ color: 'var(--gold)' }}>
                    Week {program.current_week}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))
      )}
        </>
      )}
      </div>
    </>
  );
}
